
import React, { useState, useEffect, useCallback } from 'react';
import { HashRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Calendar as CalendarIcon, CalendarPlus, LayoutDashboard, Loader2 } from 'lucide-react';
import { apiRequest } from './services/api';
import { CalendarView } from './components/CalendarView';
import { BookingForm } from './components/BookingForm';
import { AdminDashboard } from './components/AdminDashboard';
import { GlobalSettings, ConfigEntry } from './types';
import { DEFAULT_SITE_TITLE, DEFAULT_FORM_TITLE, DEFAULT_TIME_SLOTS, DEFAULT_TOPIC_OPTIONS } from './constants';

const App: React.FC = () => {
  const [initialized, setInitialized] = useState(false);
  const [settings, setSettings] = useState<GlobalSettings>({
    siteTitle: DEFAULT_SITE_TITLE,
    formTitle: DEFAULT_FORM_TITLE,
    timeSlots: DEFAULT_TIME_SLOTS.split(','),
    topicOptions: [],
    notice: '',
    minNotice: 0,
    maxAdvance: 365,
    allConfigs: []
  });

  const loadGlobalSettings = useCallback(async () => {
    // 透過 API 獲取最準確的設定 (不受 CSV 過濾影響)
    const res = await apiRequest<ConfigEntry[]>('getConfigs');
    if (res.success && res.data) {
      const configs = res.data;
      const find = (t: string) => configs.find(c => c.type === t)?.value;
      const topics = find('TOPIC_OPTION') || find('TOPIC_OPTIONS') || DEFAULT_TOPIC_OPTIONS;
      const advance = find('MAX_ADVANCE') || find('MAX_ADVANCE_DAYS') || '365';
      
      setSettings({
        siteTitle: find('SITE_TITLE') || DEFAULT_SITE_TITLE,
        formTitle: find('FORM_TITLE') || DEFAULT_FORM_TITLE,
        timeSlots: (find('TIME_SLOTS') || DEFAULT_TIME_SLOTS).split(','),
        topicOptions: topics ? topics.split(',').filter(t => t.trim() !== '') : [],
        notice: find('NOTICE') || '',
        minNotice: parseInt(find('MIN_NOTICE') || '0'),
        maxAdvance: parseInt(advance),
        allConfigs: configs
      });
    }
    setInitialized(true);
  }, []);

  useEffect(() => {
    loadGlobalSettings();
  }, [loadGlobalSettings]);

  if (!initialized) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
          <p className="font-black text-slate-400 tracking-widest uppercase text-xs">Initializing System...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <nav className="h-20 bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-[60] px-4 sm:px-8">
          <div className="max-w-7xl mx-auto h-full flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-all group">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-200 group-hover:rotate-6 transition-transform">
                <CalendarIcon className="w-6 h-6" />
              </div>
              <span className="text-xl font-black tracking-tight text-slate-800">{settings.siteTitle}</span>
            </Link>

            <div className="flex items-center gap-1 sm:gap-2">
              <Link to="/" className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-slate-600 hover:bg-slate-50 font-bold transition-all" title="預約日曆">
                <CalendarIcon className="w-5 h-5" />
                <span className="hidden md:inline">預約日曆</span>
              </Link>
              <Link to="/book" className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-slate-600 hover:bg-slate-50 font-bold transition-all" title="開始預約">
                <CalendarPlus className="w-5 h-5" />
                <span className="hidden md:inline">開始預約</span>
              </Link>
              <div className="w-px h-6 bg-slate-200 mx-2 hidden sm:block"></div>
              <Link to="/admin" className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-900 transition-all shadow-md shadow-slate-100" title="管理介面">
                <LayoutDashboard className="w-5 h-5" />
                <span className="hidden md:inline">管理後台</span>
              </Link>
            </div>
          </div>
        </nav>

        <main className="flex-1 p-4 sm:p-8 md:p-12">
          <div className="max-w-7xl mx-auto">
            <Routes>
              <Route path="/" element={<CalendarView settings={settings} />} />
              <Route path="/book" element={<BookingForm settings={settings} />} />
              <Route path="/admin" element={<AdminDashboard onConfigChange={loadGlobalSettings} />} />
            </Routes>
          </div>
        </main>

        <footer className="py-12 bg-white border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-8 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3 opacity-50">
              <CalendarIcon className="w-5 h-5" />
              <span className="font-bold">{settings.siteTitle}</span>
            </div>
            <div className="text-slate-400 text-sm font-medium">
              © {new Date().getFullYear()} ConsultFlow Pro. 版權所有.
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
};

export default App;
